// src/features/templates/templatesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getListOfProductTemplates,
  getCategoriesAndSubcategories,
  searchAndFilterTemplates,
  getSingleTemplate,
  createProductTemplate,
  updateProductTemplate,
  deleteTemplatesAdmin 
} from '../../services/productTemplates';

// Thunk to fetch templates
export const fetchTemplates = createAsyncThunk(
  'templates/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getListOfProductTemplates();
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

// Thunk to fetch categories and subcategories
export const fetchCategoriesAndSubcategories = createAsyncThunk(
  'templates/fetchCategoriesAndSubcategories',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getCategoriesAndSubcategories();
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const searchAndFilterFromAllTemplate = createAsyncThunk(
  'templates/searchAndFilterTemplates',
  async ({ searchTerm, category, subCategory, status }, { rejectWithValue }) => {
    try {
      const data = await searchAndFilterTemplates(searchTerm, category, subCategory, status);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const fetchSingleTemplate = createAsyncThunk(
  'templates/fetchSingleTemplate',
  async (templateId, { rejectWithValue }) => {
    try {
      const data = await getSingleTemplate(templateId);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    } 
  }
);

export const createTemplate = createAsyncThunk(
  'templates/createTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const data = await createProductTemplate(templateData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const updateTemplate = createAsyncThunk(
  'templates/updateTemplate',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateProductTemplate(id, data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const deleteTemplates = createAsyncThunk(
  'templates/deleteTemplates',
  async (templateIds, { rejectWithValue }) => {
    try {
      const response = await deleteTemplatesAdmin(templateIds);
      return response; // The response is { deleted: [...], not_deleted: [...] }
    } catch (error) {
      // Capture the specific error message from the backend
      if (error.response && error.response.data && error.response.data.error) {
          return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue(error.message);
    }
  }
);

const templatesSlice = createSlice({
  name: 'templates',
  initialState: {
    loading: false,
    error: null,
    data: [],
    filteredData: [],
    categories: [],
    subcategories: [],
    categoriesLoading: false,
    categoriesError: null,
    searchLoading: false,
    searchError: null,
    currentTemplate: null,
    currentTemplateLoading: false,
    currentTemplateError: null,
    createLoading: false,
    createError: null,
    updateLoading: false,
    updateError: null,
     deleteLoading: false,
    deleteError: null,
    currentFilters: {
      searchTerm: '',
      category: [],
      subCategory: [],
      status: ''
    },
  },
  reducers: {
    updateFilters: (state, action) => {
      state.currentFilters = {
        ...state.currentFilters,
        ...action.payload
      };
    },
    resetFilters: (state) => {
      state.currentFilters = {
        searchTerm: '',
        category: [],
        subCategory: [],
        status: ''
      };
      state.filteredData = []; // Clear filtered data
    },
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
     clearDeleteError: (state) => {
        state.deleteError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Templates reducers
      .addCase(fetchTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Categories reducers
      .addCase(fetchCategoriesAndSubcategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchCategoriesAndSubcategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload.map(category => ({
          id: category.id,
          name: category.category_name,
        }));
        state.subcategories = action.payload.flatMap(category => 
          category.sub_categories.map(sub => ({
            id: sub.id,
            name: sub.sub_category_name,
            categoryId: category.id,
          }))
        );
      })
      .addCase(fetchCategoriesAndSubcategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload;
      })
      
      // Search and filter reducers
      .addCase(searchAndFilterFromAllTemplate.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchAndFilterFromAllTemplate.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.filteredData = action.payload;
      })
      .addCase(searchAndFilterFromAllTemplate.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
      })
      
      // Single template reducers
      .addCase(fetchSingleTemplate.pending, (state) => {
        state.currentTemplateLoading = true;
        state.currentTemplateError = null;
      })
      .addCase(fetchSingleTemplate.fulfilled, (state, action) => {
        state.currentTemplateLoading = false;
        state.currentTemplate = action.payload;
      })
      .addCase(fetchSingleTemplate.rejected, (state, action) => {
        state.currentTemplateLoading = false;
        state.currentTemplateError = action.payload;
      })
      
      // Create template reducers
      .addCase(createTemplate.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.createLoading = false;
        state.data = [action.payload, ...state.data];
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })
      
      // Update template reducers
      .addCase(updateTemplate.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.data = state.data.map(template => 
          template.id === action.payload.id ? action.payload : template
        );
        state.currentTemplate = action.payload;
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      .addCase(deleteTemplates.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteTemplates.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Get the array of successfully deleted IDs from the backend response
        const deletedIds = action.payload.deleted || [];
        // Filter the state to remove only the templates that were actually deleted
        state.data = state.data.filter(template => !deletedIds.includes(template.id));
        state.filteredData = state.filteredData.filter(template => !deletedIds.includes(template.id));
      })
      .addCase(deleteTemplates.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      });
  },
});

export const { updateFilters, resetFilters, clearCurrentTemplate, clearDeleteError } = templatesSlice.actions;
export default templatesSlice.reducer;



